import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { LearnArticleLink, LearnSeriesNav } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.como-saber-si-mi-restaurante-es-rentable.${field}`

const SEGITTUR_HREF =
    'https://www.segittur.es/transformacion-digital/proyectos-transformacion-digital/plan-de-activacion-digital-del-sector-horeca-turistico/index.html'
const EL_ESPANOL_CIERRES_HREF =
    'https://www.elespanol.com/madrid/ocio/20250518/naufragio-restauracion-madrid-cierres-mitad-restaurantes-no-duran-anos/1003743758183_0.html'

const SOURCES = [
    {
        href: SEGITTUR_HREF,
        labelKey: 'sources.1',
        fallback:
            'Plan de Activación Digital del sector HORECA turístico e informe «La digitalización en el canal HORECA turístico en 2026», SEGITTUR y Hostelería de España, con EY, publicado el 16 de junio de 2026',
    },
    {
        href: EL_ESPANOL_CIERRES_HREF,
        labelKey: 'sources.2',
        fallback:
            'El Español, recuento de cierres de restaurantes en Madrid de Fernando Múgica, publicado el 18 de mayo de 2025',
    },
] as const

export function ComoSaberSiMiRestauranteEsRentableArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'cabeza', label: t(k('toc.1'), '1. «Los números los llevo aquí»') },
        { id: 'escapa', label: t(k('toc.2'), '2. Lo que la cabeza lleva bien y lo que se le escapa') },
        { id: 'noventa', label: t(k('toc.3'), '3. Nueve de cada diez gestiona sin cuenta de resultados') },
        { id: 'tamano', label: t(k('toc.4'), '4. Si la estimación fuera buena, ¿de cuántos bares hablamos?') },
        { id: 'deje', label: t(k('toc.5'), '5. «Ya me metí en un programa y lo dejé»') },
        { id: 'plato', label: t(k('toc.6'), '6. El plato que se te fue sin que lo notaras') },
        { id: 'niveles', label: t(k('toc.7'), '7. Empieza por el nivel en el que estás') },
        { id: 'semana', label: t(k('toc.8'), '8. Tres cosas que puedes hacer esta semana sin pagar nada') },
        { id: 'matices', label: t(k('toc.9'), '9. Lo que este estudio no dice') },
    ]

    const weekItems = [
        {
            lead: t(k('week.1.lead'), 'Escandalla tus diez platos más vendidos.'),
            rest: t(
                k('week.1.rest'),
                ' Solo esos diez, y con el albarán de esta semana, no con el precio que recuerdas.',
            ),
        },
        {
            lead: t(
                k('week.2.lead'),
                'Coge tus diez ingredientes principales y compara el último albarán con uno de hace seis meses.',
            ),
            rest: t(k('week.2.rest'), ' Ahí está el movimiento lento, en euros.'),
        },
        {
            lead: t(k('week.3.lead'), 'Calcula el food cost global del mes:'),
            rest: t(
                k('week.3.rest'),
                ' género consumido dividido entre ventas sin IVA. Existencias del día 1, más compras, menos existencias del día 30.',
            ),
        },
    ]

    const maturityRows = [
        {
            level: t(k('table.row1.level'), 'Tradicional'),
            how: t(
                k('table.row1.how'),
                'Gestionas por intuición y experiencia, los números están en tu cabeza, la contabilidad la ve el gestor y tú conoces el resultado tres meses tarde',
            ),
        },
        {
            level: t(k('table.row2.level'), 'En evolución'),
            how: t(
                k('table.row2.how'),
                'Tienes TPV y alguna hoja de cálculo, sabes tu food cost global aproximado pero no el de cada plato, y miras los números cuando algo va mal',
            ),
        },
        {
            level: t(k('table.row3.level'), 'Conectado'),
            how: t(
                k('table.row3.how'),
                'Tienes el coste por plato actualizado, revisas el resultado cada mes y mueves precios o escandallos a partir de ese número',
            ),
        },
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'Un consultor de hostelería estima que alrededor del 90% de los hosteleros no tiene cuenta de resultados. Cómo saber si tu restaurante es rentable sin comprar nada, y por dónde se empieza: escandallar tus diez platos más vendidos.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Hostelero revisando albaranes y números del restaurante en una mesa tras el servicio',
                    )}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                <span className={articleUi.em}>
                                    {t(
                                        k('lead'),
                                        'Un consultor de hostelería estima que alrededor del 90% de los hosteleros no tiene cuenta de resultados. Lo dice dentro del estudio de digitalización del canal HORECA que Hostelería de España y SEGITTUR publicaron el 16 de junio de 2026. En ese mismo estudio, solo el 50% del perfil de hostelero tradicional revisa cada mes si su negocio es rentable. Y el 73% de los que ya digitalizaron algo dice que no ha ahorrado ni la mitad de su tiempo de gestión. Aquí tienes cómo saber si tu restaurante es rentable sin comprar nada, y la acción por la que se empieza: escandallar tus diez platos más vendidos con el albarán de esta semana.',
                                    )}
                                </span>
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='cabeza' className={articleUi.h2}>
                        {t(k('h2.cabeza'), '«Los números los llevo aquí»')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cabeza.1'),
                                'Te lo han preguntado mil veces y contestas lo mismo, señalándote la sien.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cabeza.2'),
                                'No es una bravata. Sabes lo que te cuesta la caja de tomate, sabes qué días entra gente y sabes, con un margen de cien euros, lo que va a haber en el cajón un viernes de octubre.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cabeza.3'),
                                'Llevas años haciéndolo así y el negocio sigue abierto. Eso también es un dato.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.cabeza.4'),
                                'La pregunta no es si tu cabeza funciona. Es qué parte del negocio cabe dentro.',
                            )}
                        </p>
                    </div>

                    <h2 id='escapa' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.escapa'), 'Lo que la cabeza lleva bien y lo que se le escapa')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.escapa.1'),
                                'La cabeza lleva bien lo que se repite y lo que duele: el alquiler, la nómina, el precio de los cuatro géneros que compras cada semana.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.escapa.2'), 'Lo que no lleva es el movimiento lento.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.escapa.3'),
                                'Un proveedor te sube la carne un 4% en marzo, otro el vino un 3% en junio, otro la guarnición un 5% en septiembre. Ninguna de las tres subidas te hace levantar la cabeza del albarán.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.escapa.4Em'),
                                    'Las tres juntas son un 12% más caro en el plato que más vendes, y tu carta sigue con el precio de enero.',
                                )}
                            </span>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.escapa.5'),
                                'Eso no se ve en el cajón. Se ve tres meses después, cuando el gestor cierra el trimestre, el número sale peor de lo que esperabas y ya no hay forma de saber cuál de los cuarenta platos se lo comió.',
                            )}
                        </p>
                    </div>

                    <h2 id='noventa' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.noventa'), 'Nueve de cada diez gestiona sin cuenta de resultados')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.noventa.1'), 'Aquí va la cifra, y va con su letra pequeña por delante.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.noventa.2Before'),
                                'En el estudio «La digitalización en el canal HORECA turístico en 2026», que ',
                            )}
                            <a
                                href={SEGITTUR_HREF}
                                target='_blank'
                                rel='noopener noreferrer'
                                className={articleUi.link}
                            >
                                {t(k('p.noventa.2Link'), 'Hostelería de España y SEGITTUR publicaron en junio de 2026')}
                            </a>
                            {t(
                                k('p.noventa.2Mid'),
                                ', Paco Cruz, consultor de hostelería y director de The Food Manager, estima, a partir de su experiencia formando a hosteleros, que ',
                            )}
                            <span className={articleUi.em}>
                                {t(k('p.noventa.2Em'), 'alrededor del 90% no dispone de una cuenta de resultados')}
                            </span>
                            {t(k('p.noventa.2After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>{t(k('p.noventa.3Em'), 'Eso no es una encuesta.')}</span>{' '}
                            {t(
                                k('p.noventa.3'),
                                'Es la estimación de un profesional que lleva años dando formación en el sector, recogida dentro de un informe institucional. No hay muestra ni margen de error detrás de ese número, y quien te lo venda como si fuera del INE te lo está vendiendo mal.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.noventa.4Before'),
                                'Lo que sí hay es un segundo dato, medido con otra vara, apuntando al mismo sitio: en el perfil de hostelero tradicional que dibuja ese estudio, ',
                            )}
                            <span className={articleUi.em}>
                                {t(k('p.noventa.4Em'), 'solo el 50% revisa mensualmente si su negocio es rentable')}
                            </span>
                            {t(k('p.noventa.4After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.noventa.5'),
                                'Una cuenta de resultados es la lista de lo que entra y lo que sale en un mes, ordenada, sin IVA y contando el género consumido en vez del género comprado. Eso es todo lo que es.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.noventa.6Before'),
                                'No confundas este 90% con otra cifra distinta que también usamos en Tipi: el ',
                            )}
                            <span className={articleUi.em}>{t(k('p.noventa.6Em'), '90% sin presupuesto anual')}</span>
                            {t(k('p.noventa.6Mid'), ' que aparece en ')}
                            <LearnArticleLink slug='controlar-numeros-restaurante-no-delegar'>
                                {t(k('p.noventa.6Link'), 'por qué no puedes delegar el control de los números')}
                            </LearnArticleLink>
                            {t(
                                k('p.noventa.6After'),
                                '. Misma cifra, distinto significado: allí es Adrià hablando de presupuesto; aquí es una estimación de campo sobre cuenta de resultados.',
                            )}
                        </p>
                    </div>

                    <h2 id='tamano' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.tamano'), 'Si la estimación fuera buena, ¿de cuántos bares hablamos?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.tamano.1'),
                                'España tiene más de 300.000 establecimientos de hostelería, según el Anuario de Hostelería de España.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.tamano.2Em'),
                                    'Aplicar ese 90% da unos 270.000 locales gestionados sin saber si ganan dinero.',
                                )}
                            </span>{' '}
                            <em>
                                {t(
                                    k('p.tamano.2Note'),
                                    'Cuenta nuestra sobre una estimación ajena: sirve para hacerse a la idea del tamaño, no para citarla como censo.',
                                )}
                            </em>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.tamano.3Before'),
                                'Ponlo al lado de otra cifra distinta. En Madrid, según el recuento de cierres que lleva Fernando Múgica y que ',
                            )}
                            <a
                                href={EL_ESPANOL_CIERRES_HREF}
                                target='_blank'
                                rel='noopener noreferrer'
                                className={articleUi.link}
                            >
                                {t(k('p.tamano.3Link'), 'publicó El Español en mayo de 2025')}
                            </a>
                            {t(
                                k('p.tamano.3After'),
                                ', el 63% de los locales que echaron el cierre no había llegado a los cinco años.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.tamano.4'),
                                'Son dos cosas separadas y nadie ha demostrado que una cause la otra. Pero es el suelo sobre el que trabajas.',
                            )}
                        </p>
                    </div>

                    <h2 id='deje' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.deje'), '«Ya me metí en un programa y lo dejé»')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.deje.1'),
                                'Si probaste algo y lo abandonaste, no eres la excepción. Eres el caso frecuente.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.deje.2Em'),
                                    'El 73% de los hosteleros dice que la digitalización no le ha ahorrado ni la mitad del tiempo que dedica a la gestión',
                                )}
                            </span>
                            {t(k('p.deje.2'), ', según datos de TheFork recogidos en el estudio.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.deje.3Before'), 'Y ')}
                            <span className={articleUi.em}>
                                {t(
                                    k('p.deje.3Em'),
                                    'el 30% señala la falta de formación digital como obstáculo principal',
                                )}
                            </span>
                            {t(k('p.deje.3After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.deje.4'),
                                'El propio estudio lo remata con la frase de uno de los hosteleros entrevistados: «no es falta de interés, es falta de tiempo y acompañamiento».',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.deje.5'),
                                'Que es otra manera de decir que no te vendieron un método. Te vendieron una pantalla.',
                            )}
                        </p>
                    </div>

                    <h2 id='plato' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.plato'), 'El plato que se te fue sin que lo notaras')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.plato.1'), 'Un ejemplo con números redondos. ')}
                            <em>
                                {t(
                                    k('p.plato.1Note'),
                                    'Carta inventada para explicar el mecanismo, no es el escandallo de ningún local.',
                                )}
                            </em>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.plato.2'),
                                'Carrilleras. Las pusiste a 16,50 € en enero, con un coste de 5,20 €. Food cost del 31,5%, que está bien.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.plato.3'),
                                'En septiembre la carne ha subido 0,90 €, el vino de la salsa 0,20 € y la guarnición 0,15 €.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.plato.4Em'),
                                    'El plato cuesta ahora 6,45 € y en la carta sigue a 16,50 €. El food cost real es del 39%.',
                                )}
                            </span>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.plato.5Before'),
                                'Son 1,25 € menos por plato. Si vendes 25 a la semana, ese plato deja ',
                            )}
                            <span className={articleUi.em}>{t(k('p.plato.5Em'), '1.625 € menos al año')}</span>
                            {t(
                                k('p.plato.5After'),
                                ' que en enero. Un plato. De cuarenta. Ese es el dinero que no aparece en ningún sitio hasta que alguien va a buscarlo.',
                            )}
                        </p>
                    </div>

                    <h2 id='niveles' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(
                            k('h2.niveles'),
                            '¿Cómo sé si mi restaurante es rentable? Empieza por el nivel en el que estás',
                        )}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.niveles.1'),
                                'El estudio de Hostelería de España y SEGITTUR clasifica al hostelero en tres niveles de madurez. Traducidos a segunda persona:',
                            )}
                        </p>
                    </div>

                    <div className={articleUi.tableWrap}>
                        <table className={cn(articleUi.tableFacts, '[&_thead_th]:text-left')}>
                            <caption className='sr-only'>
                                {t(k('table.caption'), 'Tres niveles de madurez del hostelero')}
                            </caption>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('table.col.level'), 'Nivel')}</th>
                                    <th scope='col'>{t(k('table.col.how'), 'Cómo se reconoce')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {maturityRows.map(row => (
                                    <tr key={row.level}>
                                        <th scope='row'>{row.level}</th>
                                        <td>{row.how}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.niveles.2Em'),
                                    'La distancia entre el primer nivel y el segundo no es comprar software: es escandallar diez platos una vez.',
                                )}
                            </span>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.niveles.3Before'),
                                'Si lo que te falta es la cadencia (semanal, mensual, trimestral), no el primer termómetro, eso lo desmontamos en ',
                            )}
                            <LearnArticleLink slug='cada-cuanto-revisar-numeros-restaurante'>
                                {t(k('p.niveles.3Link'), 'cada cuánto hay que mirar los números del restaurante')}
                            </LearnArticleLink>
                            {t(k('p.niveles.3After'), '.')}
                        </p>
                    </div>

                    <h2 id='semana' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.semana'), 'Tres cosas que puedes hacer esta semana sin pagar nada')}
                    </h2>
                    <ol className={articleUi.numListGap2xl}>
                        {weekItems.map(item => (
                            <li key={item.lead} className={articleUi.numItem}>
                                <strong>{item.lead}</strong>
                                {item.rest}
                            </li>
                        ))}
                    </ol>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.semana.1Before'),
                                'Cuando tengas ese resultado viene la siguiente pregunta, que es cuánto de ese dinero puedes sacar de verdad: la desmontamos entera en ',
                            )}
                            <LearnArticleLink slug='beneficio-real-restaurante-cuanto-puedo-sacar'>
                                {t(k('p.semana.1Link'), 'el artículo sobre el beneficio real de un restaurante')}
                            </LearnArticleLink>
                            {t(k('p.semana.1After'), '.')}
                        </p>
                    </div>

                    <h2 id='matices' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.matices'), 'Lo que este estudio no dice')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.matices.1Em'),
                                    'Es una investigación cualitativa, no una encuesta representativa.',
                                )}
                            </span>{' '}
                            {t(
                                k('p.matices.1'),
                                '49 entrevistas a actores clave, 6 focus groups, 18 acompañamientos en el día a día y 97 agentes del canal. Sólido para entender por qué pasan las cosas, insuficiente para dar porcentajes nacionales.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(k('p.matices.2Em'), 'El 90% es la estimación de campo de un consultor')}
                            </span>
                            {t(k('p.matices.2'), ', no una medición con muestra.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(k('p.matices.3Em'), 'El dato de cierres es de Madrid y de un recuento privado')}
                            </span>
                            {t(
                                k('p.matices.3'),
                                ', no de un registro oficial ni de una serie nacional. Y no dice que los locales cierren por no controlar costes: dice cuántos cerraron y cuánto duraron.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.4'),
                                'Y el ejemplo de las carrilleras es nuestro. Tu carta tiene otros números.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.5'),
                                'Tipi figura entre las empresas tecnológicas colaboradoras del estudio. Eso no cambia la letra pequeña de los datos; sí explica por qué los conocemos de primera mano.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('closing.signoff'),
                                'El precio de la carta lo pones tú. Es lo único de toda esta historia que no depende de un proveedor.',
                            )}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>{t(k('h2.cta'), 'Empieza por un plato')}</h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.subBefore'),
                            'Escandallar un plato es gratis y se hace en una tarde. Si quieres el número sin montarte la hoja de cálculo desde cero, empieza por ',
                        )}
                        <LearnArticleLink slug='como-hacer-un-escandallo-paso-a-paso'>
                            {t(k('cta.subLink'), 'cómo hacer un escandallo paso a paso')}
                        </LearnArticleLink>
                        {t(k('cta.subAfter'), ' o cuéntanoslo y le ponemos números.')}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                </section>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={articleUi.link}
                                >
                                    {t(k(source.labelKey), source.fallback)}
                                </a>
                            </li>
                        ))}
                        <li>
                            {t(
                                k('sources.3'),
                                'Estimación del 90% sin cuenta de resultados y datos de ahorro de tiempo (73%) y formación digital (30%): Paco Cruz / TheFork, recogidos en el informe SEGITTUR y Hostelería de España',
                            )}
                        </li>
                        <li>
                            {t(
                                k('sources.4'),
                                'Número de establecimientos de hostelería en España: Anuario de Hostelería de España, Hostelería de España',
                            )}
                        </li>
                        <li>
                            {t(
                                k('sources.5'),
                                'Ejemplo numérico y reparto sobre 300.000 establecimientos: elaboración propia de Tipi',
                            )}
                        </li>
                    </ul>
                </section>
            </article>
        </div>
    )
}
